import { useId } from "react";
import { cn } from "../lib/cn.js";

interface SectionProps {
  title: string;
  children: React.ReactNode;
  /** Rendered on the heading row, right-aligned. */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * A titled panel.
 *
 * `<section aria-labelledby>` rather than a `<div>`, because the browser suite
 * reaches two of these as `getByRole("region", { name: "Telemetry" })` and
 * `{ name: "Live" }` and then asserts an absence *inside* them -- so the
 * labelled section is what gives those assertions something to scope to.
 * Switching it to a `<div>`, or dropping the `aria-labelledby`, silently
 * removes the region role and the tests fail on the locator rather than on
 * what they were checking.
 *
 * The heading text and the section's accessible name are therefore the same
 * string by construction, which is also what the navigation contract needs:
 * a nav label and its page heading must match.
 */
export function PageSection({ title, children, actions, className }: SectionProps) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className={cn("rounded-card border border-line bg-card p-6 sm:p-7", className)}
    >
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h2 id={headingId} className="text-lg font-semibold tracking-tight text-ink">
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** A two-column list of label and value. */
export function DataList({
  children,
  columns = 2,
}: {
  children: React.ReactNode;
  columns?: 1 | 2;
}) {
  return (
    <dl className={cn("grid grid-cols-1 gap-x-6 gap-y-3", columns === 2 && "sm:grid-cols-2")}>
      {children}
    </dl>
  );
}

export function DataRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line pb-2">
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="tabular text-sm font-medium text-ink">{children}</dd>
    </div>
  );
}
